// Ported from horizontal-actions-dashboard/frontend/src/api/client.ts + queries.ts.
// The standalone dashboard used react-query + a bespoke fetch client against a
// FastAPI backend at http://localhost:8000/api/... . In this app we reuse the
// shared authFetch()/API_BASE_URL pattern (see WarehouseAvailability.tsx) and the
// Flask backend's ported routes under /api/horizontal-actions/...
//
// There is no react-query here, so callers (the Terminals* components) manage
// their own loading/error/data state with useState/useEffect, matching the rest
// of this app's convention.

import { authFetch } from '../services/authFetch';
import { API_BASE_URL } from '../apiClient';
import type {
  MetadataResponse,
  KpiSummary,
  RegionSummary,
  TerminalSummary,
  RegionDetailResponse,
  TerminalDetailResponse,
  DiagnosticsResponse,
  RefreshResponse,
  MatrixResponse,
  CellEditRequest,
  ActionMetaEditRequest,
  AddActionRequest,
} from './horizontalActionsTypes';

const HORIZONTAL_ACTIONS_BASE = `${API_BASE_URL}/api/horizontal-actions`;

export type ApiErrorKind =
  | 'backend_unavailable'
  | 'workbook_missing'
  | 'malformed_workbook'
  | 'not_found'
  | 'conflict'
  | 'unknown';

export class ApiError extends Error {
  status: number;
  kind: ApiErrorKind;
  /** Set on a 500: quote it when reporting the problem, so it can be found in the server log. */
  correlationId?: string;
  /** Set on a 409: the version the server holds now. */
  currentVersion?: number;

  constructor(
    message: string,
    status: number,
    kind: ApiErrorKind,
    extra?: { correlationId?: string; currentVersion?: number },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
    this.correlationId = extra?.correlationId;
    this.currentVersion = extra?.currentVersion;
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 0) return 'backend_unavailable';
  if (status === 503) return 'workbook_missing';
  if (status === 500) return 'malformed_workbook';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  return 'unknown';
}

function buildQuery(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null);
  if (entries.length === 0) return '';
  const search = new URLSearchParams();
  entries.forEach(([k, v]) => search.set(k, String(v)));
  return `?${search.toString()}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await authFetch(`${HORIZONTAL_ACTIONS_BASE}${path}`, init);
  } catch (err) {
    throw new ApiError('Unable to reach the backend service.', 0, 'backend_unavailable');
  }

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    let correlationId: string | undefined;
    let currentVersion: number | undefined;
    try {
      const body = await response.json();
      // The server's own wording is what the user sees (for example why a date was refused).
      if (body && typeof body.detail === 'string') {
        message = body.detail;
      } else if (body && typeof body.message === 'string') {
        message = body.message;
      }
      if (body && typeof body.correlation_id === 'string') correlationId = body.correlation_id;
      if (body && typeof body.current_version === 'number') currentVersion = body.current_version;
    } catch {
      // response body wasn't JSON — fall back to the generic message
    }
    throw new ApiError(message, response.status, kindForStatus(response.status), { correlationId, currentVersion });
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return (await response.json()) as T;
}

function requestWithBody<T>(path: string, method: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

// --- Read endpoints --------------------------------------------------------

export function getMetadata(): Promise<MetadataResponse> {
  return request<MetadataResponse>('/metadata');
}

export function getSummary(): Promise<KpiSummary> {
  return request<KpiSummary>('/summary');
}

export function getRegions(): Promise<RegionSummary[]> {
  return request<RegionSummary[]>('/regions');
}

export function getRegionDetail(regionCode: string): Promise<RegionDetailResponse> {
  return request<RegionDetailResponse>(`/regions/${encodeURIComponent(regionCode)}`);
}

export function getTerminals(sort?: string): Promise<TerminalSummary[]> {
  return request<TerminalSummary[]>(`/terminals${buildQuery({ sort })}`);
}

export function getTerminalDetail(terminalName: string): Promise<TerminalDetailResponse> {
  return request<TerminalDetailResponse>(`/terminals/${encodeURIComponent(terminalName)}`);
}

export function getDiagnostics(): Promise<DiagnosticsResponse> {
  return request<DiagnosticsResponse>('/diagnostics');
}

export function refresh(): Promise<RefreshResponse> {
  return requestWithBody<RefreshResponse>('/refresh', 'POST');
}

// --- Action Matrix -----------------------------------------------------------

export function getMatrix(): Promise<MatrixResponse> {
  return request<MatrixResponse>('/matrix');
}

export function editMatrixCell(payload: CellEditRequest): Promise<MatrixResponse> {
  return requestWithBody<MatrixResponse>('/matrix/cell', 'PATCH', payload);
}

export function editMatrixAction(payload: ActionMetaEditRequest): Promise<MatrixResponse> {
  return requestWithBody<MatrixResponse>('/matrix/action', 'PATCH', payload);
}

export function addMatrixAction(payload: AddActionRequest): Promise<MatrixResponse> {
  return requestWithBody<MatrixResponse>('/matrix/action', 'POST', payload);
}

export interface JiraSyncQueued {
  success: boolean;
  action_id: string;
  status: string;
  message: string;
  correlation_id: string | null;
}

/**
 * Send one Action to Jira with the title the user reviewed. This is the only call that
 * creates Jira items. The backend returns straight away; the worker creates them shortly after.
 */
export function sendActionToJira(actionId: string, epicTitle: string): Promise<JiraSyncQueued> {
  return requestWithBody<JiraSyncQueued>(
    `/actions/${encodeURIComponent(actionId)}/jira-send`,
    'POST',
    { epic_title: epicTitle },
  );
}

export function deleteMatrixAction(actionId: string): Promise<MatrixResponse> {
  return requestWithBody<MatrixResponse>(`/matrix/action/${encodeURIComponent(actionId)}`, 'DELETE');
}
